import {
  ArrowUpTrayIcon,
  Breadcrumb,
  Button,
  CalendarDaysIcon,
  CheckCircleIcon,
  DownloadExcel,
  ErrorAlert,
  formatNumber,
  Modal,
  modalHandler,
  ModalOpenBtn,
  PageHero,
  PrinterIcon,
  SearchToggle,
  ShieldCheckIcon,
  TableRowsPerPage,
  XCircleIcon,
} from "@";
import { DataTable } from "@/shared/components/tanstackTable";
import { useVerifyPage } from "../hooks";
import MultiSearch from "../components/MultiSearch.tsx";
import RejectOrdersModal from "../components/RejectOrdersModal.tsx";
import ResendCodeModal from "../components/ResendCodeModal.tsx";
import BulkUploadModal from "../components/BulkUploadModal";
import ReviewConsole from "../components/ReviewConsole";
import UpdateSmsSendingDateModal, {
  UPDATE_SMS_SENDING_DATE_MODAL_ID,
} from "../components/UpdateSmsSendingDateModal.tsx";
import { exportVerificationStagesToVerifyExcel } from "../api/requests/export-stages-excel.ts";
import { verifyExcelHeaders } from "../lib/options.ts";
import { ArrowPathIcon } from "@heroicons/react/16/solid";
import DropdownOptions from "../components/DropdownOptions.tsx";
import { BulkSelection } from "@/shared/components";
import { VerificationStageStatus } from "../../types.ts";

const VerifyPage = () => {
  const links = [{ name: "إدارة الجودة", link: "" }];

  const {
    data,
    columns,
    isLoading,
    error,
    totalItems,
    search,
    updateSearch,
    handleSearchSubmit,
    handleSearchReset,
    reviewState,
    reviewOrder,
    closeReview,
    handleReviewAccept,
    handleReviewReject,
    isReviewSubmitting,
    pg,
    updatePg,
    selectedIds,
    selectedStatuses,
    selectionResetKey,
    handleSelect,
    clearSelection,
    refetch,
    handleUnassignDriver,
    handleReturnToDriver,
    isUnassigning,
    isReturning,
    handleDownloadPdf,
    isDownloadingPdf,
    selectingAll,
    setSelectingAll,
    handleAcceptOrders,
    isAcceptingOrders,
  } = useVerifyPage();

  const canAcceptOrders =
    selectedIds.length > 0 &&
    selectedStatuses.some((status) =>
      [
        VerificationStageStatus.WaitingVerification,
        VerificationStageStatus.Rejected,
      ].includes(status),
    );

  const canRejectOrders =
    selectedIds.length > 0 &&
    selectedStatuses.some((status) =>
      [VerificationStageStatus.WaitingVerification].includes(status),
    );

  const canDoBulkUpload =
    selectedIds.length > 0 &&
    selectedStatuses.every((status) =>
      [
        VerificationStageStatus.Assigned,
        VerificationStageStatus.Rejected,
      ].includes(status),
    );

  const hasSelection = selectedIds.length > 0;

  return (
    <>
      <ErrorAlert reload={refetch} show={error} />
      <Breadcrumb links={links} />

      {/* Review console */}
      <ReviewConsole
        key={
          reviewState.open
            ? `${reviewState.verificationStageId}-${reviewState.attachmentIndex}`
            : "closed"
        }
        visible={reviewState.open}
        order={reviewOrder}
        initialAttachmentIndex={reviewState.attachmentIndex}
        onClose={closeReview}
        onAccept={handleReviewAccept}
        onReject={handleReviewReject}
        isSubmitting={isReviewSubmitting}
      />

      {/* Modals */}
      <Modal id="reject-orders-modal" close={() => {}} title="رفض الطلبات">
        <RejectOrdersModal
          searchValues={search.values}
          selectingAll={selectingAll}
          ids={selectedIds}
          onSuccess={() => {
            modalHandler("reject-orders-modal");
            clearSelection();
          }}
        />
      </Modal>

      <Modal
        size="max-w-4xl"
        id="bulk-upload-modal"
        close={() => {}}
        title="التوثيق الجماعي"
      >
        <BulkUploadModal
          ids={selectedIds}
          selectingAll={selectingAll}
          searchValues={search.values}
          onSuccess={() => {
            clearSelection();
          }}
          totalItems={totalItems}
        />
      </Modal>

      <Modal
        fullHeight
        id="resend-verification-modal"
        close={() => {}}
        title="اعادة إرسال التوثيق"
      >
        <ResendCodeModal />
      </Modal>

      <Modal
        fullHeight
        id={UPDATE_SMS_SENDING_DATE_MODAL_ID}
        close={() => {}}
        title="تحديث تاريخ التنفيذ"
      >
        <UpdateSmsSendingDateModal
          ids={selectedIds}
          selectingAll={selectingAll}
          searchValues={search.values}
          onSuccess={clearSelection}
        />
      </Modal>

      <main className="table-card relative">
        <PageHero
          eyebrow="العمليات / الجودة"
          title="إدارة الجودة"
          count={totalItems}
          icon={ShieldCheckIcon}
          hideCount={isLoading}
        >
          <TableRowsPerPage
            rowsPerPageOptions={[5, 10, 50, 100, 300, 500, 1000]}
            pagination={{ ...pg, rows: totalItems }}
            onRowsPerPageChange={(size: number) => updatePg({ size, page: 1 })}
          />

          <ModalOpenBtn
            modalTitleClassName="md:inline-block hidden"
            disabled={data.length <= 0 || !data}
            target="resend-verification-modal"
            style="btn-green"
            title="اعادة إرسال "
          >
            <ArrowPathIcon className="h-5 w-5" />
          </ModalOpenBtn>

          <DownloadExcel
            length={data?.length}
            search={search.values}
            headerCellsName={verifyExcelHeaders}
            fetch={exportVerificationStagesToVerifyExcel}
          />

          <SearchToggle toggle={() => updateSearch({ show: !search.show })} />
        </PageHero>

        {/* Multi Search inputs */}
        <MultiSearch
          search={search}
          onSubmit={handleSearchSubmit}
          updateSearch={updateSearch}
          onReset={handleSearchReset}
        />

        {/* Action toolbar */}
        <div className="relative mt-4 rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-white p-2 dark:border-gray-700/60 dark:from-purple-950/20 dark:via-gray-800 dark:to-gray-800 md:mt-6">
          {/* Selection state badge — own row on mobile */}
          {hasSelection && (
            <div className="mb-2 flex items-center gap-2 px-1 lg:mb-0 lg:hidden">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold tabular-nums text-purple-900 ring-1 ring-purple-200 dark:bg-purple-950/50 dark:text-purple-300 dark:ring-purple-800/40">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                {formatNumber(selectedIds.length)} محدد
              </span>
            </div>
          )}

          {/* Buttons grid — 2 cols on mobile, inline on lg+ */}
          <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center [&>*]:!w-full lg:[&>*]:!w-fit">
            {/* Inline selection badge for lg+ */}
            {hasSelection && (
              <span className="!hidden lg:me-1 lg:!inline-flex lg:items-center lg:gap-1.5 lg:rounded-full lg:bg-purple-100 lg:px-2.5 lg:py-1 lg:text-xs lg:font-bold lg:tabular-nums lg:text-purple-900 lg:ring-1 lg:ring-purple-200 dark:lg:bg-purple-950/50 dark:lg:text-purple-300 dark:lg:ring-purple-800/40">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                {formatNumber(selectedIds.length)} محدد
              </span>
            )}

            <Button
              type="button"
              isLoading={isAcceptingOrders}
              onClick={handleAcceptOrders}
              title="تاكيد"
              disabled={!canAcceptOrders}
              className="btn-green !w-full !justify-center lg:!w-fit"
            >
              <CheckCircleIcon className="h-5 w-5" />
            </Button>

            <ModalOpenBtn
              title="رفض"
              target="reject-orders-modal"
              disabled={!canRejectOrders}
              style="btn-red !w-full lg:!w-fit !justify-center"
            >
              <XCircleIcon className="h-5 w-5" />
            </ModalOpenBtn>

            {/* Divider — desktop only */}
            <span
              aria-hidden
              className="!hidden lg:!block lg:h-7 lg:!w-px lg:bg-gradient-to-b lg:from-transparent lg:via-gray-200 lg:to-transparent dark:lg:via-gray-700"
            />

            <Button
              type="button"
              title="طباعة"
              className="btn-outline-green !w-full !justify-center lg:!w-fit"
              disabled={!hasSelection}
              isLoading={isDownloadingPdf}
              onClick={handleDownloadPdf}
            >
              <PrinterIcon className="h-5 w-5" />
            </Button>

            <ModalOpenBtn
              title="التوثيق الجماعي"
              target="bulk-upload-modal"
              disabled={!canDoBulkUpload}
              variant="btn-outline-green"
              style="!w-full lg:!w-fit !justify-center"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </ModalOpenBtn>

            <ModalOpenBtn
              variant="btn-outline-green"
              style="!w-full lg:!w-fit !justify-center"
              title="تحديث تاريخ التنفيذ"
              target={UPDATE_SMS_SENDING_DATE_MODAL_ID}
              disabled={!hasSelection}
            >
              <CalendarDaysIcon className="h-5 w-5" />
            </ModalOpenBtn>

            {/* Divider — desktop only */}
            <span
              aria-hidden
              className="!hidden lg:!block lg:h-7 lg:!w-px lg:bg-gradient-to-b lg:from-transparent lg:via-gray-200 lg:to-transparent dark:lg:via-gray-700"
            />

            <DropdownOptions
              selectingAll={selectingAll}
              totalItems={totalItems}
              selectedIds={selectedIds}
              selectedStatuses={selectedStatuses}
              isReturning={isReturning}
              isUnassigning={isUnassigning}
              handleReturnToDriver={handleReturnToDriver}
              handleUnassignDriver={handleUnassignDriver}
            />

            {/* Refresh — full row on mobile, pushed to end on lg */}
            <button
              onClick={refetch}
              className="btn-gust col-span-2 inline-flex items-center justify-center gap-2 text-base lg:col-span-1 lg:ms-auto lg:!w-fit"
              title="تحديث"
            >
              <ArrowPathIcon className="h-5 w-5 transition-transform duration-500 hover:-rotate-180" />
              تحديث
            </button>
          </div>
        </div>

        {selectedIds.length === pg.size && (
          <BulkSelection
            selectedIds={selectedIds}
            totalItems={totalItems}
            clearSelection={clearSelection}
            selectingAll={selectingAll}
            setSelectingAll={setSelectingAll}
          />
        )}

        {/* Table */}
        <DataTable
          data={data}
          columns={columns}
          isLoading={isLoading}
          pagination={{ ...pg, rows: totalItems }}
          onPageChange={(page: number) => updatePg({ page })}
          onPageSizeChange={(size: number) => updatePg({ size, page: 1 })}
          selectable
          onSelectedChange={handleSelect}
          selectionResetKey={selectionResetKey}
        />
      </main>
    </>
  );
};

export default VerifyPage;
